// core/settings — cache config aplikasi (tabel settings). Dimuat sekali saat login,
// dibaca sinkron oleh modul/dokumen. Write via saveSetting (upsert + perbarui cache).
import { supabase } from './supabase';
import { pesanDbError } from './dberr';
import { LOGO_IFICS } from '@ui/logo';
import { DEFAULT_SLA_ON, DEFAULT_SLA_CFG, DEFAULT_FINANCE_SLA } from '@domain/sla';
import { TARIF_KOSONG, type TarifPotongan } from '@domain/gaji';
import { DEFAULT_STORAGE_THR } from '@domain/storagefee';

export interface Profil { namaUsaha: string; tagline: string; telp: string; wa: string; qris: string; rekening: string; email: string; instagram: string; website: string; alamatPusat: string; supplierWa: string;
  /** Akun sosial media — dicetak sebagai ajakan "ikuti kami" di nota & tanda terima. */
  tiktok?: string; facebook?: string;
  /** Alamat resmi aplikasi utk tautan/QR di dokumen. Kosong = ikut alamat yang sedang dipakai. */
  alamatPublik?: string;
  /** Satu tautan yang memuat SEMUA tautan toko (Linktree dsb). Jadi isi QR "ikuti kami" —
   *  satu QR, bukan satu QR per akun, supaya dokumen tetap muat di ½ A4. */
  sosmedHub?: string;
  /** Logo toko sebagai data URI gambar. Kosong = pakai logo resmi iFiCs bawaan. */
  logo?: string }
export interface Survei { on: boolean; base: string }
/**
 * Template WhatsApp — SEMUA pesan WA aplikasi, bukan hanya ke pelanggan (Apo 20 Jul).
 * Sebelumnya 9 pesan terkunci di dalam kode sehingga Apo tak bisa mengubahnya sama sekali.
 * Placeholder ditulis {kurungKurawal} dan diisi `fillTpl` saat kirim.
 */
export interface WaTpl {
  // ── Ke pelanggan (alur servis) ──
  terima: string; persetujuan: string; siap: string; serah: string; tagih: string; resend: string;
  siapLaporan: string;      // dari Laporan DP: unit sudah siap, mohon diambil
  refund: string;           // pengembalian dana selesai diproses
  survei: string;           // minta penilaian layanan
  terimaKasih: string;      // CRM — sapaan terima kasih pelanggan
  broadcast: string;        // CRM — pesan blast promo
  ultah: string;            // CRM — ucapan ulang tahun
  ajakAfiliasi: string;     // CRM — ajakan jadi perekomendasi (afiliasi)
  pelangganKosong: string;  // pembuka bebas dari menu Pelanggan
  // ── Internal & supplier ──
  akunKaryawan: string;     // kirim akun login ke karyawan baru
  keCs: string;             // teknisi → CS: hasil diagnosa, minta CS konfirmasi ke pelanggan
  keTeknisi: string;        // CS → teknisi: pelanggan setuju / unit siap dikerjakan
  keFinance: string;        // → finance: minta verifikasi pembayaran / refund
  keSupplier: string;       // → supplier: permintaan sparepart (nota pengambilan)
  supplierHutang: string;   // ringkasan/statement hutang ke supplier
  dokumenLink: string;      // → pelanggan: tautan dokumen (nota / surat kuasa)
}

/** Kelompok untuk dropdown editor — label ramah + urutan tampil. */
export const WA_TPL_META: { key: keyof WaTpl; grup: string; label: string; ph: string[] }[] = [
  { key: 'terima', grup: 'Pelanggan', label: 'Unit diterima', ph: ['merk', 'tipe', 'kerusakan', 'nama', 'no', 'tracking'] },
  { key: 'persetujuan', grup: 'Pelanggan', label: 'Minta persetujuan biaya', ph: ['merk', 'tipe', 'kerusakan', 'nama', 'no', 'est', 'lama', 'ket'] },
  { key: 'siap', grup: 'Pelanggan', label: 'Nota / total tagihan', ph: ['merk', 'tipe', 'nama', 'no', 'total'] },
  { key: 'serah', grup: 'Pelanggan', label: 'Serah terima (terima kasih + survei + garansi + sosmed)', ph: ['nama', 'no', 'unit', 'toko', 'garansi', 'survei', 'sosmed', 'tagline'] },
  { key: 'siapLaporan', grup: 'Pelanggan', label: 'Unit siap diambil (pengingat)', ph: ['merk', 'tipe', 'nama', 'no', 'unit'] },
  { key: 'dokumenLink', grup: 'Pelanggan', label: 'Kirim dokumen (tautan)', ph: ['nama', 'no', 'merk', 'tipe', 'jenis', 'total', 'link'] },
  { key: 'tagih', grup: 'Pelanggan', label: 'Tagih piutang', ph: ['merk', 'tipe', 'nama', 'no', 'sisa', 'jt'] },
  { key: 'refund', grup: 'Pelanggan', label: 'Pengembalian dana', ph: ['merk', 'tipe', 'nama', 'no', 'jumlah', 'via'] },
  { key: 'resend', grup: 'Pelanggan', label: 'Kirim ulang link lacak', ph: ['merk', 'tipe', 'nama', 'no', 'tracking'] },
  { key: 'survei', grup: 'Pelanggan', label: 'Minta penilaian (survei)', ph: ['merk', 'tipe', 'nama', 'link'] },
  { key: 'terimaKasih', grup: 'Pelanggan', label: 'Sapaan terima kasih (CRM)', ph: ['nama'] },
  { key: 'broadcast', grup: 'Pelanggan', label: 'Broadcast promo & poin (CRM)', ph: ['nama', 'poin', 'hangus', 'hadiah', 'link'] },
  { key: 'ultah', grup: 'Pelanggan', label: 'Ucapan ulang tahun', ph: ['nama', 'poin', 'link'] },
  { key: 'ajakAfiliasi', grup: 'Pelanggan', label: 'Ajakan jadi perekomendasi', ph: ['nama', 'bonus', 'poin', 'link'] },
  { key: 'pelangganKosong', grup: 'Pelanggan', label: 'Pembuka bebas (menu Pelanggan)', ph: ['nama'] },
  { key: 'akunKaryawan', grup: 'Internal', label: 'Kirim akun login karyawan', ph: ['nama', 'user', 'pass'] },
  { key: 'keCs', grup: 'Internal', label: '→ CS (hasil diagnosa)', ph: ['no', 'pelanggan', 'merk', 'tipe', 'kerusakan', 'est', 'lama', 'ket', 'teknisi'] },
  { key: 'keTeknisi', grup: 'Internal', label: '→ Teknisi (disetujui / dikerjakan)', ph: ['no', 'pelanggan', 'merk', 'tipe', 'kerusakan', 'est', 'teknisi', 'cabang'] },
  { key: 'keFinance', grup: 'Internal', label: '→ Finance (perlu verifikasi)', ph: ['no', 'pelanggan', 'merk', 'tipe', 'jumlah', 'cara', 'perihal'] },
  { key: 'supplierHutang', grup: 'Supplier', label: 'Ringkasan hutang ke supplier', ph: ['nama', 'sisa'] },
  { key: 'keSupplier', grup: 'Supplier', label: '→ Supplier (permintaan sparepart)', ph: ['supplier', 'no', 'merk', 'tipe', 'items', 'cabang'] },
];
export interface Kebijakan {
  biayaSimpanBulan: number; biayaSimpanOn: boolean; ambangBelum: number;
  slaOn: boolean; slaCfg: Record<string, number>;
  serahWajibBarcode: boolean; printSize: 'A5' | 'A4';
  /** Jam sebelum sparepart yang sudah diserahkan dianggap OTOMATIS diterima teknisi (0128). */
  autoTerimaJam: number;
  // SLA FINANCE (menit) + penagihan (hari lewat tempo) — settable di tab SLA & Waktu.
  slaVerifikasiBayarMenit: number; slaVerifikasiRefundMenit: number; slaApprovalMenit: number; slaPenagihanEskalasiHari: number;
  // Ambang waktu biaya-simpan (hari) — sebelumnya hardcoded 90/180/30.
  simpanMulaiHari: number; simpanLepasHari: number; simpanIntervalHari: number;
  // Aging piutang buckets (hari) — sebelumnya hardcoded 30/60/90.
  agingB1Hari: number; agingB2Hari: number; agingB3Hari: number;
  /** Jatuh tempo default piutang (hari) — terisi otomatis saat kasir pilih Piutang / Tempo. */
  tempoHari: number;
  /** Baris per halaman tabel (paginasi klien semua menu). 0 = tanpa paginasi. */
  rowsPerPage: number;
  /** Auto-hapus SELFIE absensi lebih tua dari N hari (0 = mati). Rekap kehadiran tetap utuh. */
  selfieRetensiHari: number;
  /** Masa berlaku TAUTAN dokumen yang dikirim ke pelanggan lewat WA (hari). Lewat itu tautan mati. */
  dokLinkHari: number;
  /** Jeda minimum sebelum pelanggan yang sama boleh ditagih/diingatkan lagi (hari). 0 = tanpa jeda. */
  jedaIngatHari: number;
  /** Kelonggaran setelah tanggal estimasi selesai sebelum tiket disebut TERLAMBAT (hari). */
  graceEstimasiHari: number;
  /** Berapa hari tiket boleh menunggu persetujuan pelanggan sebelum CS diingatkan (hari). */
  staleKonfirmasiHari: number;
  /** Isian Terima Servis yang belum jadi tiket dibuang dari perangkat setelah N hari. */
  draftKedaluwarsaHari: number;
  /** Auto-logout setelah tak ada aktivitas N menit (0 = mati). Username tetap saat login lagi. */
  autoLogoutMenit: number;
}

export const DEFAULT_PROFIL: Profil = { namaUsaha: 'iFiCs', tagline: 'I Fix · I Care · You Smile', telp: '', wa: '', qris: 'Tunjukkan QRIS di kasir (minta ke CS)', rekening: 'BCA 1234567890 a/n iFiCs', email: '', instagram: '', website: '', alamatPusat: '', supplierWa: '', tiktok: '', facebook: '', sosmedHub: '', logo: '' };
// base kosong = ikut alamat aplikasi yang dideploy (documents.doc.survei fallback ke
// `${alamatPublik()}/survei.html#t=`). Isi manual di Pengaturan hanya bila mau domain khusus.
export const DEFAULT_SURVEI: Survei = { on: true, base: '' };
/**
 * Format pesan: BERBARIS "Label : nilai" (permintaan Apo 20 Jul, dgn contohnya sendiri) —
 * satu paragraf panjang berantakan dibaca di HP; bentuk berbaris langsung terbaca sekilas.
 * `\n` di sini menjadi baris baru sungguhan di WhatsApp (di-encode oleh core/wa).
 */
export const DEFAULT_WA_TPL: WaTpl = {
  terima:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'Kerusakan : {kerusakan}\n' +
    'No. Servis : {no}\n\n' +
    'Sudah kami terima 🙏\n' +
    'Lacak status : {tracking}\n' +
    '— iFiCs',
  persetujuan:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'Kerusakan : {kerusakan}\n' +
    'No. Servis : {no}\n\n' +
    'Hasil pemeriksaan : {ket}\n' +
    'Estimasi biaya : {est}\n' +
    'Estimasi selesai : {lama}\n\n' +
    'Mohon konfirmasi SETUJU / TIDAK 🙏\n' +
    '— iFiCs',
  siap:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'No. Servis : {no}\n\n' +
    'Servis SELESAI ✅\n' +
    'Total biaya : {total}\n\n' +
    'Terima kasih 🙏\n' +
    '— iFiCs',
  serah:
    'Halo {nama} 🙏\n' +
    'Terima kasih sudah mempercayakan servis ke {toko}!\n' +
    'Unit {unit} (No. {no}) sudah selesai & diterima.\n\n' +
    '🛡️ Garansi: {garansi}\n\n' +
    '⭐ Mohon beri penilaian layanan kami (ikut undian doorprize!):\n{survei}\n\n' +
    '📲 Follow & pantau info + promo kami:\n{sosmed}\n\n' +
    '{tagline}',
  siapLaporan:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'No. Servis : {no}\n\n' +
    'Unit sudah SIAP DIAMBIL ✅\n' +
    'Mohon segera diambil ya 🙏\n' +
    '— iFiCs',
  tagih:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'No. Servis : {no}\n\n' +
    'Sisa tagihan : {sisa}{jt}\n' +
    'Mohon info pelunasannya ya 🙏\n' +
    '— iFiCs',
  refund:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'No. Servis : {no}\n\n' +
    'Pengembalian dana : {jumlah}\n' +
    'Via : {via}\n\n' +
    'Sudah kami proses 🙏\n' +
    '— iFiCs',
  resend:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'No. Servis : {no}\n\n' +
    'Lacak status servis : {tracking}\n' +
    '— iFiCs',
  survei:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n\n' +
    'Terima kasih sudah servis di iFiCs 🙏\n' +
    'Mohon beri penilaian layanan kami (1 menit) :\n{link}',
  terimaKasih: 'Halo {nama}, terima kasih sudah menjadi pelanggan iFiCs 🙏\n',
  broadcast:
    'Halo {nama} 👋\n\n' +
    'Poin loyalti Anda : {poin} poin\n' +
    'Hangus terdekat : {hangus}\n\n' +
    'Bisa ditukar dengan:\n{hadiah}\n\n' +
    'Cek poin & tukar hadiah:\n{link}\n\n' +
    'Ditunggu kedatangannya 🙏\n' +
    '— iFiCs',
  ultah:
    'Selamat ulang tahun, {nama} 🎂🎉\n\n' +
    'Semoga sehat & sukses selalu.\n' +
    'Poin loyalti Anda saat ini : {poin} poin\n\n' +
    'Mampir ya, ada hadiah menunggu ditukar 🎁\n{link}\n' +
    '— iFiCs',
  ajakAfiliasi:
    'Halo {nama} 👋\n\n' +
    'Ajak teman servis di iFiCs — Anda dapat {bonus} poin tiap teman yang servis pertama kali.\n' +
    'Cukup sebutkan nama Anda saat teman mendaftar di kasir.\n\n' +
    'Poin Anda sekarang : {poin}\n' +
    'Cek & tukar hadiah : {link}\n' +
    '— iFiCs',
  pelangganKosong: 'Halo {nama}, ',
  akunKaryawan:
    'Akun iFiCs Anda\n\n' +
    'Nama : {nama}\n' +
    'Username : {user}\n' +
    'Password sementara : {pass}\n\n' +
    'Mohon segera ganti password setelah masuk 🙏',
  keCs:
    '[INTERNAL → CS]\n\n' +
    'No. Servis : {no}\n' +
    'Pelanggan : {pelanggan}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'Kerusakan : {kerusakan}\n\n' +
    'Hasil pemeriksaan : {ket}\n' +
    'Estimasi biaya : {est}\n' +
    'Estimasi selesai : {lama}\n' +
    'Teknisi : {teknisi}\n\n' +
    'Mohon dikonfirmasi ke pelanggan 🙏',
  keTeknisi:
    '[INTERNAL → TEKNISI]\n\n' +
    'No. Servis : {no}\n' +
    'Pelanggan : {pelanggan}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'Kerusakan : {kerusakan}\n\n' +
    'Pelanggan SETUJU — total {est}\n' +
    'Teknisi : {teknisi}\n' +
    'Cabang : {cabang}\n\n' +
    'Mohon segera dikerjakan 🙏',
  keFinance:
    '[INTERNAL → FINANCE]\n\n' +
    'No. Servis : {no}\n' +
    'Pelanggan : {pelanggan}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n\n' +
    'Perihal : {perihal}\n' +
    'Jumlah : {jumlah}\n' +
    'Cara bayar : {cara}\n\n' +
    'Mohon diverifikasi 🙏',
  keSupplier:
    'Halo {supplier} 🙏\n\n' +
    'Permintaan sparepart\n' +
    'No. Servis : {no}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'Barang : {items}\n' +
    'Cabang : {cabang}\n\n' +
    'Mohon disiapkan ya 🙏\n' +
    '— iFiCs',
  dokumenLink:
    'Nama pelanggan : {nama}\n' +
    'Merk handphone : {merk}\n' +
    'Tipe : {tipe}\n' +
    'No. Servis : {no}\n' +
    'Total : {total}\n\n' +
    'Berikut {jenis} Anda 🙏\n' +
    'Buka / simpan di sini :\n{link}\n\n' +
    'Simpan sebagai bukti servis & klaim garansi ya.\n' +
    '— iFiCs',
  supplierHutang:
    'Halo {nama} 🙏\n\n' +
    'Ringkasan hutang iFiCs kepada Anda\n' +
    'Sisa hutang : {sisa}\n\n' +
    'Kartu hutang lengkap kami kirim terpisah.\n' +
    '— iFiCs',
};
export const DEFAULT_KEBIJAKAN: Kebijakan = {
  biayaSimpanBulan: 50000, biayaSimpanOn: true, ambangBelum: 30,
  slaOn: DEFAULT_SLA_ON, slaCfg: DEFAULT_SLA_CFG, serahWajibBarcode: false, printSize: 'A5', autoTerimaJam: 24,
  slaVerifikasiBayarMenit: DEFAULT_FINANCE_SLA.verifikasiBayarMenit,
  slaVerifikasiRefundMenit: DEFAULT_FINANCE_SLA.verifikasiRefundMenit,
  slaApprovalMenit: DEFAULT_FINANCE_SLA.approvalMenit,
  slaPenagihanEskalasiHari: DEFAULT_FINANCE_SLA.penagihanEskalasiHari,
  simpanMulaiHari: DEFAULT_STORAGE_THR.mulaiHari, simpanLepasHari: DEFAULT_STORAGE_THR.lepasHari, simpanIntervalHari: DEFAULT_STORAGE_THR.intervalHari,
  agingB1Hari: 30, agingB2Hari: 60, agingB3Hari: 90,
  tempoHari: 14, rowsPerPage: 30, selfieRetensiHari: 90, dokLinkHari: 365, jedaIngatHari: 3,
  graceEstimasiHari: 1, staleKonfirmasiHari: 1, draftKedaluwarsaHari: 7, autoLogoutMenit: 30,
};
export const DEFAULT_SK_KUASA = [
  'Pengambil menyatakan benar-benar diberi kuasa oleh pemilik untuk mengambil unit ini.',
  'Identitas pengambil telah diperiksa dan didokumentasikan oleh petugas.',
  'Setelah unit diserahkan, tanggung jawab beralih sepenuhnya kepada pengambil.',
  'Segala tuntutan pemilik atas pengambilan ini menjadi urusan pemilik dengan pengambil.',
];
export const DEFAULT_SK_TRACKING = [
  'Status di kartu ini menampilkan kondisi terakhir yang tercatat di sistem kami.',
  'Estimasi waktu & biaya dapat berubah setelah pemeriksaan lanjutan; perubahan akan kami konfirmasikan.',
  'Pengambilan unit tetap mengacu pada Tanda Terima Servis, bukan kartu ini.',
  // Pemberitahuan biaya simpan harus ikut di kartu ini: sesudah Tanda Terima, inilah satu-satunya
  // dokumen yang dipegang pelanggan. Tanpa ini, fee-nya ditagih tanpa pernah diberitahukan.
  'Unit yang belum diambil lebih dari 3 bulan dikenakan biaya penyimpanan; lebih dari 6 bulan di luar tanggung jawab iFiCs.',
];
export const DEFAULT_SK_TANDA = [
  'Estimasi biaya & waktu dapat berubah setelah pengecekan teknisi.',
  'Pengambilan unit wajib menunjukkan tanda terima ini.',
  'Mohon backup data; data dalam unit di luar tanggung jawab teknisi.',
  'Unit tidak diambil lebih dari 30 hari di luar tanggung jawab toko.',
  'DP yang dibayarkan tidak dikembalikan bila servis dibatalkan sepihak.',
];

// S&K KHUSUS — tampil di Tanda Terima HANYA bila "Case khusus" dicentang saat terima servis (port SK_KHUSUS v0.18).
export const DEFAULT_SK_KHUSUS = [
  'Unit dalam kondisi tidak dapat diuji fungsi penuh (mati total / masuk air / fisik rusak) sebelum servis.',
  'Kerusakan lain dapat ditemukan saat pengerjaan; estimasi dapat berubah dan akan dikonfirmasi ulang.',
  'iFiCs tidak bertanggung jawab atas kerusakan yang sudah ada namun belum terdeteksi karena kondisi unit.',
  'Risiko kerusakan bertambah selama proses perbaikan pada unit kondisi khusus menjadi tanggung jawab pemilik.',
];

let cache: Record<string, unknown> = {};

export async function loadSettings(): Promise<boolean> {
  // Gagal di sini = SELURUH setelan toko diam-diam jatuh ke bawaan (nama & logo toko, template
  // WhatsApp, kebijakan biaya simpan, hak akses). Karena itu kegagalan JANGAN mengosongkan apa
  // yang sudah ada — lebih baik memakai nilai sebelumnya daripada memakai bawaan pabrik.
  try {
    const { data, error } = await supabase().from('settings').select('key,value');
    if (error) return false;
    const baru: Record<string, unknown> = {};
    for (const row of (data ?? []) as { key: string; value: unknown }[]) baru[row.key] = row.value;
    cache = baru;
    return true;
  } catch { return false; }   // jaringan putus saat boot → pertahankan cache yang ada
}

export function getSetting<T>(key: string, def: T): T {
  return (key in cache && cache[key] != null ? cache[key] : def) as T;
}

/**
 * Baca SATU pengaturan langsung dari DB (bukan cache boot). Cache hanya dimuat sekali saat login,
 * jadi layar yang sudah lama terbuka memegang nilai basi — kalau nilai basi itu dipakai sebagai
 * dasar penyimpanan, perubahan orang lain terhapus diam-diam. Pakai ini sebelum menulis
 * pengaturan berbentuk kumpulan (template pesan, nomor WA kantor).
 */
export async function fetchSetting<T>(key: string, def: T): Promise<T> {
  const { data, error } = await supabase().from('settings').select('value').eq('key', key).maybeSingle();
  if (error) throw new Error(error.message);
  const v = (data as { value: unknown } | null)?.value;
  if (v != null) cache[key] = v;
  return (v ?? def) as T;
}

export async function saveSetting(key: string, value: unknown): Promise<void> {
  const { error } = await supabase().from('settings').upsert({ key, value, updated_at: new Date().toISOString() });
  // Error mentah RLS ("new row violates row-level security policy") tak berarti apa-apa bagi petugas —
  // penyebab terseringnya sesi kedaluwarsa, bukan kurang izin. Terjemahkan dulu (lihat core/dberr).
  if (error) throw new Error(await pesanDbError(error, 'menyimpan setelan'));
  cache[key] = value;
  // Setelan di tabel ini menggerakkan uang dan bukti: saldo awal neraca, skema komisi, berapa lama
  // selfie absensi disimpan, ambang SLA. Sebelumnya tak satu pun perubahannya meninggalkan jejak,
  // jadi tak ada cara menjawab "siapa yang mengubah ini, kapan". Dicatat di satu pintu ini supaya
  // seluruh key ikut terekam tanpa menyentuh puluhan pemanggilnya.
  // Nilainya sengaja TIDAK ikut dicatat — sebagian memuat data pribadi (profil, template WA), dan
  // jejak audit tak boleh jadi salinan kedua yang bocor lebih mudah dari aslinya.
  const { logAudit } = await import('./audit');
  await logAudit({ action: 'SETTING_WRITE', module: 'settings', refId: key, reason: `Ubah setelan: ${key}` });
}

/** Visibilitas menu per-peran (Hak Akses): {role: {menuId: false}} — hanya simpan yang DISEMBUNYIKAN. */
export const menuVis = (): Record<string, Record<string, boolean>> => getSetting('menu_vis', {});

export const profil = (): Profil => ({ ...DEFAULT_PROFIL, ...getSetting('profil', {}) });

/**
 * Logo yang dipakai dokumen & layar. Hanya menerima data URI GAMBAR — nilai lain diabaikan.
 * Setelan ini bisa ditulis Manager dan ikut ke dokumen yang dibuka pelanggan, jadi jangan
 * pernah menaruh alamat sembarang di atribut `src`. Keamanan atribut bergantung PENUH pada
 * pola di bawah: ia ter-anchor dan tak menerima kutip/spasi, sehingga tak bisa keluar atribut.
 * SVG sengaja tidak diterima — layar unggah selalu menghasilkan PNG/JPEG, jadi mengizinkannya
 * hanya menambah permukaan serang tanpa ada yang memakainya.
 */
export const logoToko = (): string => {
  const l = (profil().logo ?? '').trim();
  return /^data:image\/(png|jpeg|jpg|webp|gif);base64,[A-Za-z0-9+/=]+$/.test(l) ? l : LOGO_IFICS;
};
export const survei = (): Survei => ({ ...DEFAULT_SURVEI, ...getSetting('survei', {}) });

/** Program Undian Doorprize Survei (config di settings key 'undian'). Undi & serah di menu Undian. */
export interface Undian { on: boolean; hadiah: string; nilai: number; tglUndi: number; sk: string; kategori: string }
export const DEFAULT_UNDIAN: Undian = { on: false, hadiah: '', nilai: 0, tglUndi: 1, sk: '', kategori: 'Hadiah Loyalti' };
export const undian = (): Undian => ({ ...DEFAULT_UNDIAN, ...getSetting('undian', {}) });
export const waTpl = (): WaTpl => ({ ...DEFAULT_WA_TPL, ...getSetting('wa_tpl', {}) });
/** Tarif potongan kehadiran untuk penggajian. Default 0 = TIDAK memotong apa pun sampai diisi. */
export const tarifPotongan = (): TarifPotongan => ({ ...TARIF_KOSONG, ...getSetting('gaji', {}) });

export const kebijakan = (): Kebijakan => ({ ...DEFAULT_KEBIJAKAN, ...getSetting('kebijakan', {}) });
export const skTanda = (): string[] => getSetting('sk_tanda', DEFAULT_SK_TANDA);
/** S&K Surat Kuasa — dicetak di bawah pernyataan pelepasan tuntutan. */
export const skKuasa = (): string[] => getSetting('sk_kuasa', DEFAULT_SK_KUASA);
/** S&K Kartu Tracking — dikirim ke pelanggan lewat WA (bukan dokumen cetak). */
export const skTracking = (): string[] => getSetting('sk_tracking', DEFAULT_SK_TRACKING);
export const skKhusus = (): string[] => getSetting('sk_khusus', DEFAULT_SK_KHUSUS);

// Config Absensi (settings key 'absensi'). telat* = ambang menit; wajibSelfie/reminderMenit = fase absensi lanjutan.
export interface AbsensiCfg { telatSetengah: number; telatAlpha: number; wajibSelfie: boolean; reminderMenit: number; istirahatMenit: number }
export const DEFAULT_ABSENSI: AbsensiCfg = { telatSetengah: 30, telatAlpha: 60, wajibSelfie: true, reminderMenit: 30, istirahatMenit: 60 };
export const absensi = (): AbsensiCfg => ({ ...DEFAULT_ABSENSI, ...getSetting('absensi', {}) });
